import { SkinTypeDefStorage } from '../../storage/SkinTypeDefStorage';
import { Base64ImageStorage, extractImgSrc } from '../../storage/Base64ImageStorage';
import type { StorageInterface } from '../../stores/childNavStores';

/**
 * The subset of a SkinTypeDef's ChannelInfo settings that drive Pro Groups' own main
 * menu header bar. Each field is null when the skin doesn't define it (or the referenced
 * Base64Image record hasn't synced yet), so callers can fall back to their own defaults
 * rather than rendering an empty value.
 */
export interface SkinChannelInfo {
  logoSrc: string | null;
  backgroundSrc: string | null;
  menuFontColor: string | null;
  skinBgColor: string | null;
  /** ChannelInfo 'skintabbgcolor' — some skins (e.g. PsychScan) define only this, not skinbgcolor. */
  skinTabBgColor: string | null;
  /** ChannelInfo 'signupurl' — where the sign-in page's "Sign Up" link goes; null hides the link. */
  signUpUrl: string | null;
  /**
   * ChannelInfo 'enablereportpdf' — when on, the PDF action moves out of the Clients "View Report"
   * / profile Reports "View" columns and into the ReportView header ("View" + PDF icon, beside Back).
   */
  enableReportPdf: boolean;
}

const EMPTY_CHANNEL_INFO: SkinChannelInfo = {
  logoSrc: null,
  backgroundSrc: null,
  menuFontColor: null,
  skinBgColor: null,
  skinTabBgColor: null,
  signUpUrl: null,
  enableReportPdf: false,
};

/** Truthy ChannelInfo flag values — '1', 'true', 'yes', 'y', 'on' (case-insensitive); anything else is off. */
function isChannelInfoFlagOn(value: string): boolean {
  return ['1', 'true', 'yes', 'y', 'on'].includes(value.trim().toLowerCase());
}

/** ChannelInfo entries are {name, value} pairs — match case-insensitively, same convention as PHI field lookups. */
function findChannelInfoValue(channelInfo: Array<{ name: string; value: string }>, key: string): string {
  const keyLower = key.toLowerCase();
  return channelInfo.find((entry) => entry.name?.toLowerCase() === keyLower)?.value ?? '';
}

/**
 * Resolves the bundled SkinTypeDef content record (synced locally via SkinTypeDefStorage,
 * same bundle-sync mechanism as assessment content) for the given SkinType — normally
 * PracInfoDto.SkinType for the active account — into the ChannelInfo settings that style
 * Pro Groups' main menu header bar. logoimageid/backgroundimageid are resolved through
 * Base64ImageStorage into displayable data-URIs, the same lookup already used for the
 * expand/collapse and PDF icons elsewhere in the app.
 */
export async function resolveSkinChannelInfo(
  storage: StorageInterface,
  skinType: string,
  languageTypeId = '1'
): Promise<SkinChannelInfo> {
  if (!skinType) {
    return EMPTY_CHANNEL_INFO;
  }

  const skinDef = await new SkinTypeDefStorage(storage).getSkinTypeDef(skinType, languageTypeId);
  if (!skinDef) {
    return EMPTY_CHANNEL_INFO;
  }

  const channelInfo = skinDef.channelInfo ?? [];
  const logoImageId = findChannelInfoValue(channelInfo, 'logoimageid');
  const backgroundImageId = findChannelInfoValue(channelInfo, 'backgroundimageid');

  const imageStorage = new Base64ImageStorage(storage);
  const [logoImage, backgroundImage] = await Promise.all([
    logoImageId ? imageStorage.getImage(logoImageId) : Promise.resolve(null),
    backgroundImageId ? imageStorage.getImage(backgroundImageId) : Promise.resolve(null),
  ]);

  return {
    logoSrc: (logoImage?.base64 && extractImgSrc(logoImage.base64)) || null,
    backgroundSrc: (backgroundImage?.base64 && extractImgSrc(backgroundImage.base64)) || null,
    menuFontColor: findChannelInfoValue(channelInfo, 'menufontcolor') || null,
    skinBgColor: findChannelInfoValue(channelInfo, 'skinbgcolor') || null,
    skinTabBgColor: findChannelInfoValue(channelInfo, 'skintabbgcolor') || null,
    signUpUrl: findChannelInfoValue(channelInfo, 'signupurl') || null,
    enableReportPdf: isChannelInfoFlagOn(findChannelInfoValue(channelInfo, 'enablereportpdf')),
  };
}

let bundledSkinAssetsPromise: Promise<void> | null = null;

/**
 * Loads just the SkinTypeDef and Base64Image records from the web content bundle into local
 * storage. Skin definitions normally only land in storage once the full bundled-content
 * initialization runs — which happens after sign-in — so a signed-out visitor (fresh browser,
 * or cleared site data) has nothing for resolveSkinChannelInfo to read and would otherwise see
 * the unbranded fallback indefinitely. Web only (fetches the public asset); the promise is
 * shared so concurrent callers trigger one fetch, and a failure clears it so a later call can retry.
 */
export function ensureBundledSkinAssets(
  storage: StorageInterface,
  bundleUrl = '/assets/content/bundled-content.json'
): Promise<void> {
  if (!bundledSkinAssetsPromise) {
    bundledSkinAssetsPromise = (async () => {
      const response = await fetch(bundleUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch bundled content: ${response.status}`);
      }
      const bundle = await response.json();
      const skinDefStorage = new SkinTypeDefStorage(storage);
      const imageStorage = new Base64ImageStorage(storage);

      for (const record of bundle?.records ?? []) {
        const type = String(record?.contentType ?? '').toLowerCase();
        if ((type !== 'skintypedef' && type !== 'base64image') || !record.payload?.Data) {
          continue;
        }
        const syntheticRecord = { id: record.id, contentType: record.contentType, tableData: record.payload } as any;
        if (type === 'skintypedef') {
          await skinDefStorage.unpackFromContentRecord(syntheticRecord);
        } else {
          await imageStorage.unpackFromContentRecord(syntheticRecord);
        }
      }
    })().catch((error) => {
      bundledSkinAssetsPromise = null;
      throw error;
    });
  }
  return bundledSkinAssetsPromise;
}
